import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useTheme, alpha } from '@mui/material'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import { FaMinimize, FaMaximize } from 'react-icons/fa6'
import defaultTexts from '../../../texts/texts'
import constants from '../../../config/constants'
import { request } from '../../../_network/request'
import { billingAccountConfigsURLs } from '../../../_network/apiUrls'
import CustomBackdrop from '../../_common/CustomBackdrop'
import CustomDialog from '../../_common/CustomDialog'
import GeneralConfigView from './GeneralConfigView'
import GeneralConfigEdit from './GeneralConfigEdit'

const GeneralConfigWrapper = (props) => {
  const { setFetchAccountConfigs, accountConfigs, supportedPaymentMethods, subscriptionGroupNames } = props
  const theme = useTheme()

  const account_id = useSelector(state => state.profile.account_id)
  const user_role = useSelector(state => state.profile.user_role)
  const editPermission = user_role === 'superadmin' || user_role === 'owner' || user_role === 'admin'

  const [expanded, setExpanded] = useState(true)
  const [editMode, setEditMode] = useState(false)
  const [isLoading, setIsLoading] = useState(false)  
  const [errors, setErrors] = useState([])
  const [errorDialogOpen, setErrorDialogOpen] = useState(false)

  const [enableBillingService, setEnableBillingService] = useState(false)
  const [defaultAccountType, setDefaultAccountType] = useState('')
  const [postpaidInvoiceType, setPostpaidInvoiceType] = useState('')
  const [invoiceManager, setInvoiceManager] = useState(true)
  const [postpaidInvoicePayPeriod, setPostpaidInvoicePayPeriod] = useState('')
  const [allowPublicCalculator, setAllowPublicCalculator] = useState(true)
  const [currency, setCurrency] = useState('')
  const [enableVat, setEnableVat] = useState(true)
  const [pricesIncludeVat, setPricesIncludeVat] = useState(true)
  const [vatTax, setVatTax] = useState('')

  const wrapperStyle = {
    background: 'white',
    borderRadius: '50px 20px 20px 20px',
    boxShadow: '2px 2px 2px rgba(0, 0, 0, 0.25)',
    marginBottom: '40px',
  }

  const headStyle = {
    width: '100%',
    height: '60px',
  }

  const bodyStyle = {
    padding: '20px',
  }

  const labelStyle = {
    height: '60px',
    background: theme.palette.greenSheen,
    color: 'white',
    fontSize: '20px',
    fontWeight: '500',
    borderRadius: '50px 0px',
    padding: '0px 40px',    
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }

  const editButtonStyle = {
    fontWeight: '500',
    color: 'white',
    background: theme.palette.vodka,
    fontSize: '18px',
    height: '40px',
    borderRadius: '20px',
    padding: '0px 30px',
    textTransform: 'capitalize',
    marginTop: '20px',

    '&:hover': {
      background: alpha(theme.palette.vodka, 0.8),
    },
  }

  const saveButtonStyle = {
    fontWeight: '500',
    color: 'white',
    background: theme.palette.greenSheen,
    fontSize: '18px',
    height: '40px',
    borderRadius: '20px',
    padding: '0px 30px',
    textTransform: 'capitalize',
    marginTop: '20px',

    '&:hover': {
      background: alpha(theme.palette.greenSheen, 0.8),
    },
  }

  const getCurrencyCode = (currency) => {
    let currency_code = ''

    supportedPaymentMethods.forEach(pm => {
      if (pm.currencies && pm.currencies.length > 0) {
        pm.currencies.forEach(cu => {
          if (currency.toUpperCase() === cu.name.toUpperCase()) {
            currency_code = cu.code
          }
        })
      }
    })

    return currency_code
  }

  const handleErrorDialogOpen = () => {
    setErrorDialogOpen(true)
  }

  const handleErrorDialogClose = () => {
    setErrorDialogOpen(false)
  }

  const validateBillingForm = () => {
    let errors_list = []

    if (!defaultAccountType) {
      errors_list.push({
        title: defaultTexts.billingService + ' - ' + defaultTexts.defaultAccountType,
        field_key: 'defaultAccountType',
      })
    }

    if (!postpaidInvoiceType) {
      errors_list.push({
        title: defaultTexts.billingService + ' - ' + defaultTexts.postpaidInvoiceType,
        field_key: 'postpaidInvoiceType',
      })
    }

    if (!postpaidInvoicePayPeriod) {
      errors_list.push({
        title: defaultTexts.billingService + ' - ' + defaultTexts.postpaidInvoicePayPeriod,
        field_key: 'postpaidInvoicePayPeriod',
      })
    }

    if (!currency) {
      errors_list.push({
        title: defaultTexts.billingService + ' - ' + defaultTexts.currency,
        field_key: 'currency',
      })
    }

    if (enableVat && !vatTax) {
      errors_list.push({
        title: defaultTexts.billingService + ' - ' + defaultTexts.vatTaxPerc,
        field_key: 'vatTax',
      })
    }

    return errors_list
  }

  const validateFormFields = () => {
    let errors_list = []

    const billing_general_form_errors = validateBillingForm()
    errors_list = [...errors_list, ...billing_general_form_errors]

    setErrors(errors_list)
    
    return errors_list.length === 0 ? true : false
  }
  
  const billingAccountConfigsRequest = async (postData) => {
    const url = constants.billing_url + '/' + billingAccountConfigsURLs.getAccountConfigs
    const request_data = { url, method: 'PUT', data: postData, headers: { 'customer-account-id': account_id } }
    await request(request_data)
  }

  const handleClickEdit = () => {
    setEditMode(true)
  }

  const handleClickSave = async () => {
    setIsLoading(true)

    const form_valid = validateFormFields()

    if (form_valid) {
      const postData = {
        multiregion_support: true,
        currency: {
          name: currency,
          code: getCurrencyCode(currency),
        },
        status: enableBillingService ? 'active' : 'inactive',
        default_billing_type: defaultAccountType,
        postpaid_invoice: postpaidInvoiceType ? postpaidInvoiceType : 'auto',
        postpaid_invoice_pay_period: postpaidInvoicePayPeriod ? parseInt(postpaidInvoicePayPeriod) : 0,
        use_vat_tax: enableVat,
        price_includes_vat: pricesIncludeVat,
        vat: parseInt(vatTax),
        allow_public_price_calculator: allowPublicCalculator,
        invoice_manager: invoiceManager ? 'isender' : null,
      }

      await billingAccountConfigsRequest(postData)

      setFetchAccountConfigs(prev => prev + 1)
      setEditMode(false)
    } else {
      handleErrorDialogOpen()
    }

    setIsLoading(false)
  }

  useEffect(() => {
    if (accountConfigs && accountConfigs.status) {
      setEnableBillingService(accountConfigs.status === 'active' ? true : false)
    } else {
      setEnableBillingService(false)
    }

    if (accountConfigs && accountConfigs.default_billing_type) {
      setDefaultAccountType(accountConfigs.default_billing_type)
    } else {
      setDefaultAccountType('')
    }

    if (accountConfigs && accountConfigs.postpaid_invoice) {
      setPostpaidInvoiceType(accountConfigs.postpaid_invoice)
    } else {
      setPostpaidInvoiceType('')
    }

    if (accountConfigs && accountConfigs.postpaid_invoice_pay_period) {
      setPostpaidInvoicePayPeriod(accountConfigs.postpaid_invoice_pay_period)
    } else {
      setPostpaidInvoicePayPeriod('')
    }

    if (accountConfigs) {
      setAllowPublicCalculator(accountConfigs.allow_public_price_calculator ? true : false)
    } else {
      setAllowPublicCalculator(true)
    }

    if (accountConfigs) {
      setInvoiceManager(accountConfigs.invoice_manager ? true : false)
    } else {
      setInvoiceManager(null)
    }
    
    if (accountConfigs && accountConfigs.currency) {
      setCurrency(accountConfigs.currency.name)
    } else {
      setCurrency('')
    }

    if (accountConfigs) {
      setEnableVat(accountConfigs.use_vat_tax ? true : false)
    } else {
      setEnableVat(true)
    }

    if (accountConfigs) {
      setPricesIncludeVat(accountConfigs.price_includes_vat ? true : false)
    } else {
      setPricesIncludeVat(true)
    }

    if (accountConfigs && accountConfigs.vat) {
      setVatTax(accountConfigs.vat)
    } else {
      setVatTax('')
    }
  }, [accountConfigs])
  
  return (
    <Box sx={wrapperStyle}>
      { isLoading && <CustomBackdrop open={isLoading} /> }

      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        sx={headStyle}
      >
        <Box sx={labelStyle}>
          <Box>{defaultTexts.generalConfiguration}</Box>
        </Box>
        <Box sx={{ paddingRight: '10px ' }}>
          {
            expanded ?
            <IconButton onClick={() => setExpanded(false)}>
              <FaMinimize color={theme.palette.greenSheen} />
            </IconButton> :
            <IconButton onClick={() => setExpanded(true)}>
              <FaMaximize color={theme.palette.greenSheen} />
            </IconButton>
          }
        </Box>
      </Stack>

      {
        expanded &&
        <Box sx={bodyStyle}>
          {
            editMode ?
            <GeneralConfigEdit
              supportedPaymentMethods={supportedPaymentMethods}
              enableBillingService={enableBillingService}
              setEnableBillingService={setEnableBillingService}
              defaultAccountType={defaultAccountType}
              setDefaultAccountType={setDefaultAccountType}
              postpaidInvoiceType={postpaidInvoiceType}
              setPostpaidInvoiceType={setPostpaidInvoiceType}
              invoiceManager={invoiceManager}
              setInvoiceManager={setInvoiceManager}
              postpaidInvoicePayPeriod={postpaidInvoicePayPeriod}
              setPostpaidInvoicePayPeriod={setPostpaidInvoicePayPeriod}
              allowPublicCalculator={allowPublicCalculator}
              setAllowPublicCalculator={setAllowPublicCalculator}
              currency={currency}
              setCurrency={setCurrency}
              enableVat={enableVat}
              setEnableVat={setEnableVat}
              pricesIncludeVat={pricesIncludeVat}
              setPricesIncludeVat={setPricesIncludeVat}
              vatTax={vatTax}
              setVatTax={setVatTax}
              errors={errors}
              validateFormFields={validateFormFields}
              subscriptionGroupNames={subscriptionGroupNames}
            /> :
            <GeneralConfigView
              accountConfigs={accountConfigs}
            />
          }

          {
            editPermission &&
            <Stack
              direction='row'
              justifyContent='flex-end'
            >
              {
                editMode ?
                <Button
                  variant='contained'
                  disableRipple
                  disableElevation
                  disabled={false}
                  onClick={handleClickSave}
                  sx={saveButtonStyle}
                >
                  {defaultTexts.save}
                </Button> :
                <Button
                  variant='contained'
                  disableRipple
                  disableElevation
                  disabled={false}
                  onClick={handleClickEdit}
                  sx={editButtonStyle}
                >
                  {defaultTexts.edit}
                </Button>
              }            
            </Stack>
          }          
        </Box>
      }

      <CustomDialog
        open={errorDialogOpen}
        onClose={handleErrorDialogClose}
        dialogTitle={{ title: defaultTexts.formValidationError }}
        dialogBody={{
          text: defaultTexts.formValidationErrorText,
          sx: { paddingBottom: '20px' }
        }}
      >
        {
          errors.map((item, index) => (
            <Box key={index}>{item.title}</Box>
          ))
        }
      </CustomDialog>
    </Box>
  )
}

export default GeneralConfigWrapper